import React, { useEffect, useState } from 'react';
import { FiMail } from 'react-icons/fi';
import { useSession } from '@/hooks/useSession';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MedalContainer, MedalWrapper } from '@/components/custom/medal';
import { Loader } from '@/components/custom/loader';
import BgAluno from '@/assets/images/bg_aluno.jpg';
import { useTranslation } from 'react-i18next';
import Onboarding from '@/components/custom/onboarding';
import StudentMetricsView from './StudentMetricsView';
import { DashboardContent } from '@/layouts/dashboard';
import { ProfileBanner, ProfileBannerBg, ProfileBannerBgWrapper, ProfileBannerData, ProfileBannerHeader, ProfileBannerInfo, ProfileBannerInfoWrapper, ProfileBannerInner, ProfileBannerTitle } from '@/components/custom/profile';
import { Button } from '@/components/ui/button';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

export function lessThanThirtyDays(last_access) {
  const currentDate = new Date();
  const lastAccessDate = new Date(last_access);
  const difference = (currentDate - lastAccessDate) / (1000 * 3600 * 24);
  return difference < 30;
}

export function UsersShowStudent(props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { path, uri } = props;
  const { session } = useSession();
  const client = useClient();
  const { id } = useParams();
  const profiles = session.user?.profileNames || (session.user && session.user['profile-names']) || [];

  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStudent, setIsStudent] = useState();
  const [medals, setMedals] = useState([]);
  const [medalsLoading, setMedalsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [achievements, setAchievements] = useState([]);
  const [milestones, setMilestones] = useState();
  const [achievementRanking, setAchievementRanking] = useState();

  useEffect(() => {
    getUser();
  }, [id]);

  const lastAccess = user?.['last-access'];
  const isActive = lessThanThirtyDays(lastAccess);

  const getUser = async () => {
    setLoading(true);
    const { data, error } = await client.fetch(`users/${id}`);
    if (error) {
      toast.error(t('toast.errorGetStudentInfo'));
    } else {
      setUser(data);
      const newIsStudent = data?.['profile-names']?.[0] === 'Aluno';
      setIsStudent(newIsStudent);
      const isAdmin = !profiles?.includes('Aluno');
      setIsAdmin(isAdmin);
    }
    setLoading(false);
  };

  const getMedals = async () => {
    setMedalsLoading(true);
    const { data, error } = await client.fetch(`medals?user_id=${user.id}`);
    if (error) {
      toast.error(t('toast.errorMedals'));
    } else {
      setMedals(data);
      setMedalsLoading(false);
    }
  };

  const isOwner = () => {
    if (session?.user?.id === user?.id || isAdmin) {
      return true;
    } else {
      return false;
    }
  };

  const breadCrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [
      {
        route: uri,
        name: 'Desempenho',
        isActive: true
      }
    ]
  };

  const getAchievements = async () => {
    const { data, error } = await client.fetch(`/user_achievements?user_id=${user.id}`);
    if (error) {
      toast.error('Erro ao buscar conquistas do usuário');
    } else {
      setAchievements(data);
    }
  };

  const getMilestones = async () => {
    const { data, error } = await client.fetch(`/user_milestones?user_id=${user.id}`);
    if (error) {
      toast.error('Erro ao buscar informações');
    } else {
      setMilestones(data);
    }
  };

  useEffect(() => {
    if (user) {
      getAchievements();
      getMilestones();
      getAchievementRanking();
      getMedals();
    }
  }, [user]);

  const getAchievementRanking = async () => {
    const { data, error } = await client.fetch(`/achievement_rankings?user_id=${user.id}`);
    if (error) {
      toast.error('Erro ao buscar ranking');
    } else {
      setAchievementRanking(data);
    }
  };

  return (
    <DashboardContent breadcrumbs={breadCrumbs}>
      <ProfileBanner>
        {isAdmin && (
          <Button
            variant="outline"
            className="absolute right-3 top-3 z-10"
            wide
            asChild
          >
            <Link to={`/course/edit/participant/${id}`}>{t('button.edit')}</Link>
          </Button>
        )}

        <ProfileBannerBgWrapper>
          <ProfileBannerBg
            src={BgAluno}
            alt="imagem de fundo"
          />
        </ProfileBannerBgWrapper>

        <ProfileBannerInner>
          <AvatarWrapper
            size="xl"
            src={user?.image?.url ? user?.image?.url + `?now=${new Date().getTime()}` : null}
            text={user?.name}
            className="border-4 border-solid border-[#FDFCFA] shadow-dropshadow-sm"
          />

          <ProfileBannerHeader>
            <ProfileBannerData>
              <Badge
                variant="bright"
                size="sm"
              >
                {user?.['profile-names'] && user?.['profile-names'][0]}
              </Badge>
              {isActive ? (
                <Badge
                  variant="success"
                  size="sm"
                  className="ml-1"
                >
                  {t('journey.isActive')}
                </Badge>
              ) : (
                <Badge
                  variant="danger"
                  size="sm"
                  className="ml-1"
                >
                  {t('journey.isInactive')}
                </Badge>
              )}

              <ProfileBannerTitle>{user?.name}</ProfileBannerTitle>

              {isOwner() && (
                <ProfileBannerInfoWrapper>
                  <ProfileBannerInfo>
                    <FiMail /> {user?.email}
                  </ProfileBannerInfo>
                </ProfileBannerInfoWrapper>
              )}
            </ProfileBannerData>

            {medalsLoading && (
              <MedalWrapper className="!bg-none !p-0">
                <Loader />
              </MedalWrapper>
            )}
            {!medalsLoading && (
              <MedalWrapper
                onClick={() => navigate(`/usuario/medalhas/${id}`, { state: { isRedirected: true } })}
                className="!bg-none !p-0"
              >
                <MedalContainer
                  value={'1'}
                  src="gold"
                  size="gold"
                />
              </MedalWrapper>
            )}
          </ProfileBannerHeader>
        </ProfileBannerInner>
      </ProfileBanner>

      {isStudent ? (
        <StudentMetricsView
          achievements={achievements}
          milestones={milestones}
          achievementRanking={achievementRanking}
        />
      ) : null}

      <Onboarding
        show={showOnboarding}
        setShowOnboarding={setShowOnboarding}
        onClose={() => setShowOnboarding(false)}
        path={path}
        profiles={profiles}
        title={t('onboarding.titleShowUser')}
      />
    </DashboardContent>
  );
}
